/**
 * Framework-specific renderers for GitAgent packages.
 *
 * Each renderer transforms a parsed GitAgent package (agent.yaml + SOUL.md + RULES.md)
 * into the native file format that the target agent framework natively reads.
 *
 * Only backends with a full bridge driver (runtime execution) are included:
 *   claude-code → .claude/agents/<name>.md     (YAML frontmatter + markdown body)
 *   omp         → .omp/agents/<name>/          (directory with agent.yaml + SOUL.md)
 *   codex       → .codex/agents/<name>.toml    (TOML)
 *
 * Removed (no bridge driver): cursor, roo, github-copilot, gemini, amp.
 * Add them back when a bridge driver is implemented.
 */

import { mkdirSync, writeFileSync } from "node:fs";
import { dirname, join, relative } from "node:path";
import type { AgentManifest } from "@skaile/workspaces/core";
import { normalizeAnthropicModel } from "@skaile/workspaces/types/manifests";
import { stringify as stringifyYaml } from "yaml";
import type { AbilityRef, ContractRef } from "./fragments.js";

// ── Types ─────────────────────────────────��───────────────────────────────────

export type { AgentManifest };

/**
 * All data an {@link AgentRenderer} needs to produce a native agent definition file.
 *
 * @docLink packages/asset-manager/api-reference#renderers
 */
export interface AgentRenderInput {
  /** Parsed `agent.yaml` manifest for the GitAgent being rendered. */
  manifest: AgentManifest;
  /** Raw content of `SOUL.md`, or `null` if absent. */
  soul: string | null;
  /** Raw content of `RULES.md`, or `null` if absent. */
  rules: string | null;
  /** Raw content of `DUTIES.md`, or `null` if absent. */
  duties: string | null;
  /** Agent name used as the output filename and frontmatter `name` field. */
  agentName: string;
  /** Resolved absolute path to the framework's agent deploy directory (e.g. `.claude/agents/`). */
  agentDeployDir: string;
  /** Absolute path to the workspace root. */
  workspaceRoot: string;
  /** Resolved ability refs — installed skills referenced in the `## Installed Abilities` section. */
  abilityRefs: AbilityRef[];
  /** Resolved contract refs — installed in the skills dir, listed in the `## Contracts` section. */
  contractRefs: ContractRef[];
  /** Resolved framework fragment markdown blocks (agent-mode, handoff, skill-discovery, etc.). */
  frameworkFragments: string[];
  /** Per-project prompt extension markdown blocks loaded from `skaile.yaml` `agent.prompt-extensions`. */
  promptExtensions: string[];
}

/**
 * Result returned by every {@link AgentRenderer}.
 *
 * @docLink packages/asset-manager/api-reference#renderers
 */
export interface AgentRenderResult {
  /** Paths to files/dirs created, relative to `workspaceRoot`. */
  created: string[];
  /** Non-fatal warnings (e.g. experimental feature notices). */
  warnings: string[];
}

/**
 * A function that transforms an {@link AgentRenderInput} into framework-native agent definition files.
 *
 * @docLink packages/asset-manager/api-reference#renderers
 */
export type AgentRenderer = (input: AgentRenderInput) => AgentRenderResult;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Write an agent definition file, always overwriting if it already exists.
 * Agents are compiled artifacts — they must reflect the current canonical source.
 */
function writeAgent(
  filePath: string,
  content: string,
  workspaceRoot: string,
  created: string[],
): void {
  mkdirSync(dirname(filePath), { recursive: true });
  writeFileSync(filePath, content, "utf-8");
  created.push(relative(workspaceRoot, filePath));
}

/**
 * Build the full agent system prompt body baked into the framework-native agent file.
 *
 * This is the sole source of the agent's system prompt body. Agent identity is rendered at install time; the backend reads it natively at runtime.
 * The rendered output ends up in .claude/agents/<name>.md, .omp/agents/<name>/SOUL.md,
 * .codex/agents/<name>.toml and is read natively by the framework on every agent invocation.
 *
 * Composition order:
 *   1. SOUL.md            — agent identity and values (inlined verbatim)
 *   2. Framework fragments — agent-mode, handoff, skill-discovery, connector-usage
 *                           (conditional; see fragments.ts BUILT_IN_FRAGMENTS)
 *   3. ## Constraints     — RULES.md behavioral constraints (inlined)
 *   4. ## Duties          — DUTIES.md responsibilities (inlined)
 *   5. ## Installed Abilities — skill name + description, listed as slash commands
 *   6. ## Contracts       — relative paths to CONTRACT.md in the skills dir
 *   7. Prompt extensions  — per-project markdown from skaile.yaml agent.prompt-extensions
 */
function buildMarkdownBody(
  soul: string | null,
  rules: string | null,
  duties: string | null,
  abilityRefs: AbilityRef[],
  contractRefs: ContractRef[],
  frameworkFragments: string[],
  promptExtensions: string[],
): string {
  const parts: string[] = [];

  if (soul) parts.push(soul.trim());
  for (const fragment of frameworkFragments) parts.push(fragment);
  if (rules) parts.push(`## Constraints\n\n${rules.trim()}`);
  if (duties) parts.push(`## Duties\n\n${duties.trim()}`);

  if (abilityRefs.length > 0) {
    const lines = ["## Installed Abilities", ""];
    for (const a of abilityRefs) lines.push(`- \`/${a.name}\` — ${a.description}`);
    parts.push(lines.join("\n"));
  }

  if (contractRefs.length > 0) {
    const lines = [
      "## Contracts",
      "",
      "Read the relevant contract before acting on constrained tasks:",
      "",
    ];
    for (const c of contractRefs) lines.push(`- \`${c.relativePath}\``);
    parts.push(lines.join("\n"));
  }

  for (const ext of promptExtensions) parts.push(ext);

  return `${parts.join("\n\n")}\n`;
}

/** Escape a string for use as a TOML multi-line basic string (triple-quote). */
function toTomlMultilineString(s: string): string {
  // Replace """ with \\""" to avoid breaking the TOML literal
  return s.replace(/"""/g, '\\"\\"\\"');
}

// ── Claude Code ────────────��──────────────────────────────────────────────────
// Format: .claude/agents/<name>.md
// Sub-agent support: automatic (description-based) + @-mention
// Required frontmatter: name, description
//
// Installation → runtime handoff:
//   skaile install (claudeCodeRenderer) creates the .md file from GitAgent source
//   ClaudeSdkDriver.startQuery() passes options.agent = agentName to the SDK query
//   The SDK reads .claude/agents/<agentName>.md to get model, tools, and system prompt
//   This is equivalent to running: claude --agent <agentName>

/**
 * Render a GitAgent to Claude Code's `.claude/agents/<name>.md` format.
 *
 * Writes YAML frontmatter (name, description, model, tools, maxTurns) followed
 * by the enriched system prompt body. Always overwrites to reflect current source.
 *
 * @docLink packages/asset-manager/api-reference#renderers
 */
export const claudeCodeRenderer: AgentRenderer = ({
  manifest,
  soul,
  rules,
  duties,
  agentName,
  agentDeployDir,
  workspaceRoot,
  abilityRefs,
  contractRefs,
  frameworkFragments,
  promptExtensions,
}) => {
  const created: string[] = [];
  const warnings: string[] = [];

  const frontmatter: Record<string, unknown> = {
    name: agentName,
    description: manifest.description ?? `${agentName} agent`,
  };

  // Model: map to Claude Code's model field. Accepts aliases (sonnet/opus/haiku/inherit)
  // or a full Anthropic ID. Unrecognized values (e.g. legacy `claude-opus-4`) are
  // coerced to the matching family alias so the agent still loads.
  if (manifest.model?.preferred) {
    const raw = manifest.model.preferred;
    const normalized = normalizeAnthropicModel(raw);
    if (normalized !== raw) {
      warnings.push(
        `agent ${agentName}: model "${raw}" is not a valid Claude model ID; normalized to alias "${normalized}". Update agent.yaml.`,
      );
    }
    frontmatter.model = normalized;
  }

  // Tool restrictions
  if (manifest.tools?.allowed?.length) {
    frontmatter.tools = manifest.tools.allowed.join(", ");
  }
  if (manifest.tools?.denied?.length) {
    frontmatter.disallowedTools = manifest.tools.denied.join(", ");
  }

  // Runtime
  if (manifest.runtime?.max_turns) {
    frontmatter.maxTurns = manifest.runtime.max_turns;
  }

  // Metadata pass-through for Claude Code-specific fields
  if (manifest.metadata?.color) frontmatter.color = manifest.metadata.color;
  if (manifest.metadata?.effort) frontmatter.effort = manifest.metadata.effort;
  if (manifest.metadata?.isolation) frontmatter.isolation = manifest.metadata.isolation;

  const body = buildMarkdownBody(
    soul,
    rules,
    duties,
    abilityRefs,
    contractRefs,
    frameworkFragments,
    promptExtensions,
  );
  const content = `---\n${stringifyYaml(frontmatter).trim()}\n---\n\n${body}`;
  const filePath = join(agentDeployDir, `${agentName}.md`);
  writeAgent(filePath, content, workspaceRoot, created);

  return { created, warnings };
};

// ── OpenAI Codex ──────────────────────────────────────────────────────────────
// Format: .codex/agents/<name>.toml
// Sub-agent support: explicit user request only (not automatic)
// Required fields: name, description, developer_instructions

/**
 * Render a GitAgent to OpenAI Codex's `.codex/agents/<name>.toml` format.
 *
 * Produces a TOML file with `name`, `description`, and `developer_instructions`
 * fields. Triple-quoted TOML multi-line strings are escaped to avoid syntax errors.
 *
 * @docLink packages/asset-manager/api-reference#renderers
 */
export const codexRenderer: AgentRenderer = ({
  manifest,
  soul,
  rules,
  duties,
  agentName,
  agentDeployDir,
  workspaceRoot,
  abilityRefs,
  contractRefs,
  frameworkFragments,
  promptExtensions,
}) => {
  const created: string[] = [];
  const warnings: string[] = [];

  const instructions = buildMarkdownBody(
    soul,
    rules,
    duties,
    abilityRefs,
    contractRefs,
    frameworkFragments,
    promptExtensions,
  );
  const escaped = toTomlMultilineString(instructions);

  const lines: string[] = [
    `name = ${JSON.stringify(agentName)}`,
    `description = ${JSON.stringify(manifest.description ?? `${agentName} agent`)}`,
    `developer_instructions = """`,
    escaped.trim(),
    `"""`,
  ];
  if (manifest.model?.preferred) {
    lines.splice(2, 0, `model = ${JSON.stringify(manifest.model.preferred)}`);
  }

  const content = `${lines.join("\n")}\n`;
  const filePath = join(agentDeployDir, `${agentName}.toml`);
  writeAgent(filePath, content, workspaceRoot, created);

  // Write the full agent identity as markdown for the Codex driver to read at runtime.
  // The driver combines this with the session's dynamic content (resources, context)
  // and passes the result via model_instructions_file.
  const agentMdPath = join(dirname(agentDeployDir), "skaile-agent.md");
  writeAgent(agentMdPath, instructions, workspaceRoot, created);

  return { created, warnings };
};

// ── oh-my-pi ─��────────────��────────────────────────────────��─────────────────
// Format: .omp/agents/<name>/ (directory with agent.yaml + SOUL.md + RULES.md)
// oh-my-pi reads GitAgent directories natively via PI_CODING_AGENT_DIR.
// The renderer copies/enriches the source directory rather than flattening to .md.
//
// Installation → runtime handoff:
//   skaile install (ompRenderer) creates .omp/agents/<name>/ from GitAgent source
//   OmpDriver._start() sets env.PI_CODING_AGENT_DIR = agentDir in the child process
//   omp reads SOUL.md/RULES.md/knowledge/ from that directory natively on startup
//
// Note: REPL mode can bypass the install step — agentDir can point directly to the
// GitAgent source directory, and omp will read it natively via PI_CODING_AGENT_DIR.
// The rendered .omp/agents/ copy is only needed when the source is not accessible
// from the project cwd (e.g. different repo, remote workspace).

/**
 * Render a GitAgent to oh-my-pi's `.omp/agents/<name>/` directory format.
 *
 * Writes `agent.yaml` (with resolved model/tools/delegation) and an enriched
 * `SOUL.md` that includes fragments and extensions. omp reads this directory
 * natively via the `PI_CODING_AGENT_DIR` environment variable.
 *
 * @docLink packages/asset-manager/api-reference#renderers
 */
export const ompRenderer: AgentRenderer = ({
  manifest,
  soul,
  rules,
  duties,
  agentName,
  agentDeployDir,
  workspaceRoot,
  abilityRefs,
  contractRefs,
  frameworkFragments,
  promptExtensions,
}) => {
  const created: string[] = [];
  const warnings: string[] = [];

  const destDir = join(agentDeployDir, agentName);
  mkdirSync(destDir, { recursive: true });

  // Write agent.yaml with tools resolved
  const agentYaml: Record<string, unknown> = {
    spec_version: manifest.spec_version ?? "0.1.0",
    name: manifest.name ?? agentName,
    version: manifest.version ?? "1.0.0",
    description: manifest.description ?? `${agentName} agent`,
  };
  // Pass model through verbatim. omp takes provider-prefixed full IDs and also
  // resolves the family aliases: verified live 2026-09-20 against omp v18.1.10
  // through teamclaude.postxl.com — `--model anthropic/opus` produced
  // `model_change {"model":"anthropic/claude-opus-5","resolvedModelIsFallback":false}`.
  // (This comment previously asserted the opposite; it was never tested.)
  // Invalid IDs are caught upstream by `validateAgent` (ModelIdSchema in
  // @skaile/workspaces/types). Do NOT call `normalizeAnthropicModel` here: it
  // coerces a bad ID into an alias, turning a loud failure into a silent
  // substitution of a different model.
  if (manifest.model) agentYaml.model = manifest.model;
  if (manifest.tools) agentYaml.tools = manifest.tools;
  if (manifest.delegation) agentYaml.delegation = manifest.delegation;
  if (manifest.runtime) agentYaml.runtime = manifest.runtime;
  if (manifest.requires?.length) agentYaml.requires = manifest.requires;
  if (manifest.tags?.length) agentYaml.tags = manifest.tags;
  if (manifest.metadata) agentYaml.metadata = manifest.metadata;

  writeFileSync(join(destDir, "agent.yaml"), stringifyYaml(agentYaml), "utf-8");

  // Write SOUL.md (enriched with fragments and extensions, like other renderers)
  const enrichedSoul = buildMarkdownBody(
    soul,
    rules,
    duties,
    abilityRefs,
    contractRefs,
    frameworkFragments,
    promptExtensions,
  );
  writeFileSync(join(destDir, "SOUL.md"), enrichedSoul, "utf-8");

  created.push(relative(workspaceRoot, destDir));
  return { created, warnings };
};

// ── Renderer registry ──────────────────────────────���──────────────────────────

export type Log = (msg: string) => void;
const noop: Log = () => {};

/**
 * Render an agent to framework-native format via the renderer registry.
 *
 * Shared by both the simple deploy path (`deployAsset`) and the enriched scaffold path (agents layer).
 *
 * @param input - Full render input including manifest, SOUL/RULES text, and resolved refs.
 * @param driverTarget - Agent framework ID to look up in {@link AGENT_RENDERERS}.
 * @param log - Optional logger callback for progress messages.
 * @returns Object with `created` file paths and `warnings` list.
 * @docLink packages/asset-manager/api-reference#renderers
 */
export function renderAgentToFramework(
  input: AgentRenderInput,
  driverTarget: string,
  log: Log = noop,
): { created: string[]; warnings: string[] } {
  const renderer = AGENT_RENDERERS[driverTarget];
  if (!renderer) {
    return { created: [], warnings: [`No renderer for framework "${driverTarget}"`] };
  }
  const result = renderer(input);
  for (const c of result.created) log(`  rendered agent: ${c}`);
  for (const w of result.warnings) log(`  warn: ${w}`);
  return result;
}

/**
 * Registry mapping framework IDs to their {@link AgentRenderer} functions.
 *
 * Only backends with a full bridge driver are registered. Add new entries
 * when a bridge driver is implemented in `workspaces/bridge/src/drivers/`.
 *
 * @docLink packages/asset-manager/api-reference#renderers
 */
export const AGENT_RENDERERS: Record<string, AgentRenderer> = {
  "claude-code": claudeCodeRenderer,
  omp: ompRenderer,
  codex: codexRenderer,
};

/**
 * Returns `true` if the given driver target supports file-based sub-agent installation.
 *
 * @param driverTarget - Agent framework ID to check.
 * @returns `true` when `driverTarget` has a registered entry in {@link AGENT_RENDERERS}.
 * @docLink packages/asset-manager/api-reference#renderers
 */
export function driverTargetSupportsAgents(driverTarget: string): boolean {
  return driverTarget in AGENT_RENDERERS;
}
